import chalk from 'chalk';
import ora from 'ora';
import { input, select, checkbox, confirm } from '@inquirer/prompts';
import { randomUUID } from 'node:crypto';
import { mkdirSync, writeFileSync } from 'node:fs';
import { join } from 'node:path';
import {
  generateCharterMd,
  generateToolsMd,
  lintAll,
  getAllChannelIds,
  type CharterGenerationInput,
  type ToolsGenerationInput,
  type ChannelId,
  type LintDiagnostic,
} from '@augmented/core';
import { requireTeam } from '../lib/auth-guard.js';
import { api } from '../lib/api-client.js';
import { success, error, warn, info } from '../lib/output.js';
import { isJsonMode, jsonOutput } from '../lib/globals.js';

function toSlug(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function printDiagnostics(diagnostics: LintDiagnostic[]): void {
  for (const d of diagnostics) {
    const prefix = d.severity === 'error' ? chalk.red('ERR') : chalk.yellow('WARN');
    const path = d.path ? chalk.dim(` (${d.path})`) : '';
    console.log(`  ${prefix} [${d.code}] ${d.message}${path}`);
  }
}

/** Options passed from Commander flags. */
interface InitOptions {
  name?: string;
  codeName?: string;
  description?: string;
  env?: string;
  riskTier?: string;
  budgetType?: string;
  budgetTokens?: string;
  budgetDollars?: string;
  budgetWindow?: string;
  budgetEnforcement?: string;
  channels?: string;
  logging?: string;
}

/**
 * `agt init`
 * Interactive wizard to create a new agent, generate CHARTER.md + TOOLS.md,
 * register via the API, and write files to `.augmented/<code_name>/`.
 */
export async function initCommand(opts: InitOptions): Promise<void> {
  const teamSlug = requireTeam();
  if (!teamSlug) return;

  const nonInteractive = !!opts.name;
  const json = isJsonMode();

  let displayName: string;
  let codeName: string;
  let description: string;
  let environment: 'dev' | 'stage' | 'prod';
  let riskTier: 'Low' | 'Medium' | 'High';
  let budgetType: 'tokens' | 'dollars' | 'both';
  let budgetLimitTokens: number | undefined;
  let budgetLimitDollars: number | undefined;
  let budgetLimit: number;
  let budgetWindow: 'daily' | 'weekly' | 'monthly';
  let budgetEnforcement: 'alert' | 'throttle' | 'block' | 'degrade';
  let selectedChannels: ChannelId[];
  let loggingMode: 'hash-only' | 'redacted' | 'full-local';

  if (nonInteractive) {
    displayName = opts.name!;
    codeName = opts.codeName ?? toSlug(displayName);
    description = opts.description ?? `${displayName} agent`;
    environment = (opts.env ?? 'dev') as 'dev' | 'stage' | 'prod';
    riskTier = (opts.riskTier ?? 'Low') as 'Low' | 'Medium' | 'High';
    budgetType = (opts.budgetType ?? 'tokens') as 'tokens' | 'dollars' | 'both';
    budgetLimitTokens = (budgetType === 'tokens' || budgetType === 'both')
      ? Number(opts.budgetTokens ?? '10000')
      : undefined;
    budgetLimitDollars = (budgetType === 'dollars' || budgetType === 'both')
      ? Number(opts.budgetDollars ?? '10')
      : undefined;
    budgetLimit = budgetLimitTokens ?? budgetLimitDollars ?? 10000;
    budgetWindow = (opts.budgetWindow ?? 'daily') as 'daily' | 'weekly' | 'monthly';
    budgetEnforcement = (opts.budgetEnforcement ?? 'block') as 'alert' | 'throttle' | 'block' | 'degrade';
    selectedChannels = opts.channels
      ? opts.channels.split(',').map((c) => c.trim()) as ChannelId[]
      : [];
    loggingMode = (opts.logging ?? 'redacted') as 'hash-only' | 'redacted' | 'full-local';
  } else {
    console.log(chalk.bold('\nAugmented — Agent Init Wizard\n'));

    displayName = await input({
      message: 'Agent display name:',
      validate: (v) => v.trim().length > 0 || 'Name is required',
    });

    const suggestedSlug = toSlug(displayName);
    codeName = await input({
      message: 'Code name (kebab-case):',
      default: suggestedSlug,
      validate: (v) => /^[a-z0-9][a-z0-9-]*[a-z0-9]$|^[a-z0-9]$/.test(v) || 'Must be lowercase alphanumeric with hyphens',
    });

    description = await input({
      message: 'Short description:',
      default: `${displayName} agent`,
    });

    environment = await select({
      message: 'Environment:',
      choices: [
        { value: 'dev', name: 'dev' },
        { value: 'stage', name: 'stage' },
        { value: 'prod', name: 'prod' },
      ],
    }) as 'dev' | 'stage' | 'prod';

    riskTier = await select({
      message: 'Risk tier:',
      choices: [
        { value: 'Low', name: 'Low — read-only tools, no PII' },
        { value: 'Medium', name: 'Medium — write access, limited scope' },
        { value: 'High', name: 'High — broad access, PII, external comms' },
      ],
    }) as 'Low' | 'Medium' | 'High';

    budgetType = await select({
      message: 'Budget type:',
      choices: [
        { value: 'tokens', name: 'Tokens' },
        { value: 'dollars', name: 'Dollars' },
        { value: 'both', name: 'Both' },
      ],
    }) as 'tokens' | 'dollars' | 'both';

    budgetLimit = 10000;
    budgetLimitTokens = undefined;
    budgetLimitDollars = undefined;

    if (budgetType === 'tokens' || budgetType === 'both') {
      const val = await input({
        message: 'Token budget limit:',
        default: '10000',
        validate: (v) => !isNaN(Number(v)) && Number(v) > 0 || 'Must be a positive number',
      });
      budgetLimitTokens = Number(val);
      budgetLimit = budgetLimitTokens;
    }

    if (budgetType === 'dollars' || budgetType === 'both') {
      const val = await input({
        message: 'Dollar budget limit:',
        default: '10',
        validate: (v) => !isNaN(Number(v)) && Number(v) > 0 || 'Must be a positive number',
      });
      budgetLimitDollars = Number(val);
      if (budgetType === 'dollars') budgetLimit = budgetLimitDollars;
    }

    budgetWindow = await select({
      message: 'Budget window:',
      choices: [
        { value: 'daily', name: 'Daily' },
        { value: 'weekly', name: 'Weekly' },
        { value: 'monthly', name: 'Monthly' },
      ],
    }) as 'daily' | 'weekly' | 'monthly';

    budgetEnforcement = await select({
      message: 'Budget enforcement:',
      choices: [
        { value: 'block', name: 'Block — hard stop when limit reached' },
        { value: 'throttle', name: 'Throttle — slow down near limit' },
        { value: 'alert', name: 'Alert — warn but do not stop' },
        { value: 'degrade', name: 'Degrade — switch to cheaper model' },
      ],
    }) as 'alert' | 'throttle' | 'block' | 'degrade';

    const allChannels = getAllChannelIds();
    selectedChannels = await checkbox({
      message: 'Select allowed channels:',
      choices: allChannels.map((c) => ({ value: c, name: c })),
    }) as ChannelId[];

    loggingMode = await select({
      message: 'Logging mode:',
      choices: [
        // ENG-8726: all three labels used to promise more than the runtime
        // delivers, and the operator picks a governance mode from this list.
        // `hash-only` read "content is hashed" — in the tool-call audit log such
        // an agent discloses NO target at all, not a hashed one. `redacted` read
        // "PII stripped", which nothing enforces: normal audit redaction still
        // emits the program name, the URL host and the MCP tool segment, and a
        // private host can name a customer. `full-local` read "plain text",
        // which invites the reading that it unlocks plaintext paths in the
        // control plane — no mode does; file paths are keyed-hashed for all
        // three. (CodeRabbit.)
        {
          value: 'redacted',
          name: 'Redacted — file paths keyed-hashed; tool, program and URL host still recorded',
        },
        {
          value: 'hash-only',
          name: 'Hash-only — strictest: the audit log records which tool ran, never what it touched',
        },
        {
          value: 'full-local',
          name: 'Full local — same audit redaction as Redacted; plaintext stays host-side (dev only)',
        },
      ],
    }) as 'hash-only' | 'redacted' | 'full-local';
  }

  // ── Resolve user info via API ─────────────────────────────────────────

  const spinner = ora({ text: 'Creating agent…', isSilent: json });
  spinner.start();

  let userEmail: string;
  let userId: string;
  try {
    const me = await api.get<{ id: string; email: string | null }>('/auth/me');
    userId = me.id;
    userEmail = me.email ?? me.id;
  } catch (err) {
    spinner.fail('Could not determine current user.');
    if (json) {
      jsonOutput({ ok: false, error: (err as Error).message });
    } else {
      error((err as Error).message);
    }
    process.exitCode = 1;
    return;
  }

  const agentId = randomUUID();

  // ── Generate CHARTER.md + TOOLS.md ─────────────────────────────────────

  const charterInput: CharterGenerationInput = {
    agent_id: agentId,
    code_name: codeName,
    display_name: displayName,
    environment,
    owner: {
      id: userId,
      name: userEmail,
      email: userEmail,
    },
    risk_tier: riskTier,
    logging_mode: loggingMode,
    description,
  };

  const toolsInput: ToolsGenerationInput = {
    agent_id: agentId,
    code_name: codeName,
    environment,
    owner: userEmail,
    display_name: displayName,
    logging_redaction: loggingMode,
  };

  const charterMd = generateCharterMd(charterInput);
  const toolsMd = generateToolsMd(toolsInput);

  // ── Lint ────────────────────────────────────────────────────────────────

  const lintResult = lintAll(charterMd, toolsMd);

  // ── Register via API ───────────────────────────────────────────────────

  try {
    await api.post('/agents', {
      code_name: codeName,
      display_name: displayName,
      description,
      environment,
      risk_tier: riskTier,
      channels: selectedChannels,
      charter_content: charterMd,
      charter_version: '0.1',
      tools_content: toolsMd,
      tools_version: '0.1',
    });
  } catch (err) {
    spinner.fail('Failed to register agent.');
    if (json) {
      jsonOutput({ ok: false, error: (err as Error).message });
    } else {
      error((err as Error).message);
    }
    process.exitCode = 1;
    return;
  }

  // ── Write files to .augmented/<code_name>/ ─────────────────────────────

  const agentDir = join(process.cwd(), '.augmented', codeName);
  mkdirSync(agentDir, { recursive: true });
  writeFileSync(join(agentDir, 'CHARTER.md'), charterMd);
  writeFileSync(join(agentDir, 'TOOLS.md'), toolsMd);

  spinner.succeed(`Agent "${chalk.bold(displayName)}" created.`);

  // ── Output ─────────────────────────────────────────────────────────────

  if (json) {
    jsonOutput({
      ok: true,
      agent_id: agentId,
      code_name: codeName,
      display_name: displayName,
      environment,
      risk_tier: riskTier,
      channels: selectedChannels,
      directory: agentDir,
      lint: {
        ok: lintResult.ok,
        errors: lintResult.errors.length,
        warnings: lintResult.warnings.length,
        diagnostics: [...lintResult.errors, ...lintResult.warnings],
      },
    });
    return;
  }

  console.log();
  info(`Agent ID:     ${agentId}`);
  info(`Code Name:    ${codeName}`);
  info(`Environment:  ${environment}`);
  info(`Risk Tier:    ${riskTier}`);
  info(`Channels:     ${selectedChannels.length > 0 ? selectedChannels.join(', ') : 'none'}`);
  info(`Files:        ${agentDir}/CHARTER.md, TOOLS.md`);
  console.log();

  if (lintResult.ok && lintResult.warnings.length === 0) {
    success('Lint passed — no errors or warnings.');
  } else {
    if (lintResult.errors.length > 0) {
      error(`Lint: ${lintResult.errors.length} error(s)`);
      printDiagnostics(lintResult.errors);
    }
    if (lintResult.warnings.length > 0) {
      warn(`Lint: ${lintResult.warnings.length} warning(s)`);
      printDiagnostics(lintResult.warnings);
    }
  }

  console.log();
  info(`Next steps:`);
  info(`  1. Edit ${chalk.bold('.augmented/' + codeName + '/CHARTER.md')} to refine the agent charter`);
  info(`  2. Edit ${chalk.bold('.augmented/' + codeName + '/TOOLS.md')} to add tools`);
  info(`  3. Run ${chalk.bold('agt lint')} to validate`);
  info(`  4. Run ${chalk.bold('agt deploy')} to generate deployment config`);
}
